
/*
  TIMI SELECTED ICONS v2.7 — TESTv2

  Supports standard MYO, apparel, charms and tags.
  Recognizes Icons Only / Icon(s) Only and
  Text + Icons / Text + Icon(s).

  Preserves five-icon tray, native Zepto selection,
  removal, tab navigation and small + button.
*/

(() => {
  'use strict';

  if (window.__timiSelectedIconsV27) return;
  window.__timiSelectedIconsV27 = true;

  const MAX = 5;

  const SUFFIXES = [
    'CountryClubDogMom', 'DolceVitaSummer',
    'EclecticRetro', 'alt-girl-era-set',
    'cozy-sewing-set', 'back-to-school',
    'fall-in-love-set', 'coquette-summer',
    'western-rodeo', 'preppy-summer',
    'casino-theme', 'reading-theme',
    'sewing-theme', 'pastel-theme',
    'sports-balls', 'pink-summer',
    'y2k-theme', 'y2k-edgy',
    '20-mini-icons', 'mom-hobbies',
    'lucky-girl', 'mini-lobster',
    'wedding', 'witchy', 'zodiac'
  ];

  let tray = null;
  let expanded = 1;
  let signature = '';
  let scheduled = false;
  let removing = false;
  let lastMode = '';

  function clean(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function normalizeMode(value) {
    return clean(value)
      .replace(/\(s\)/g, 's')
      .replace(/\s*\+\s*/g, ' + ');
  }

  function text(element) {
    return clean(element?.textContent);
  }

  /*
    Locate the embroidery-mode button group.
    Do not confuse it with apparel placement.
  */

  function modeButtons() {
    const buttons = [
      ...document.querySelectorAll(
        '.product-personalizer .pplr-drop-item'
      )
    ];

    const allowed = new Set([
      'text only',
      'icons only',
      'icon only',
      'text + icons',
      'text + icon',
      'arched text',
      'timi designs',
      'upload'
    ]);

    const groups = new Map();

    buttons.forEach(button => {
      const value = normalizeMode(
        button.getAttribute('data-value') ||
        button.textContent
      );

      if (!allowed.has(value)) return;

      const parent = button.parentElement;
      if (!parent) return;

      if (!groups.has(parent)) {
        groups.set(parent, []);
      }

      groups.get(parent).push(button);
    });

    const candidates = [...groups.values()]
      .filter(group => {
        const values = group.map(button =>
          normalizeMode(
            button.getAttribute('data-value') ||
            button.textContent
          )
        );

        return (
          values.includes('text only') &&
          (
            values.includes('icons only') ||
            values.includes('icon only')
          )
        );
      });

    return candidates.sort(
      (a, b) => b.length - a.length
    )[0] || [];
  }

  function isPink(button) {
    const color = getComputedStyle(button)
      .backgroundColor
      .match(/[\d.]+/g)
      ?.slice(0, 3)
      .map(Number);

    if (!color || color.length !== 3) {
      return false;
    }

    return (
      Math.abs(color[0] - 247) < 12 &&
      Math.abs(color[1] - 188) < 12 &&
      Math.abs(color[2] - 208) < 12
    );
  }

  function mode() {
    const buttons = modeButtons();

    const active = buttons.find(button =>
      button.classList.contains('active') ||
      button.classList.contains('selected') ||
      button.classList.contains('pplr_active') ||
      button.classList.contains('pplr_selected') ||
      button.getAttribute('aria-pressed') === 'true'
    ) || buttons.find(isPink);

    return normalizeMode(
      active?.getAttribute('data-value') ||
      active?.textContent
    );
  }

  function iconsOnly() {
    return [
      'icons only',
      'icon only'
    ].includes(mode());
  }

  function textAndIcons() {
    return [
      'text + icon',
      'text + icons'
    ].includes(mode());
  }

  function supported() {
    return iconsOnly() || textAndIcons();
  }

  /*
    Find Zepto's real icon tabs.
  */

  function tabGroup() {
    const groups = [
      ...document.querySelectorAll(
        '.pplr_tab_wrapper'
      )
    ];

    const candidates = groups.filter(group =>
      [...group.querySelectorAll(
        '.pplr_tab_index'
      )].some(tab => text(tab) === 'icon 1')
    );

    return candidates.find(group =>
      group.getClientRects().length > 0
    ) || candidates[0] || null;
  }

  function tabs() {
    return [
      ...(tabGroup()?.querySelectorAll(
        '.pplr_tab_index'
      ) || [])
    ];
  }

  function count() {
    const names = tabs().map(text);
    let total = 0;

    for (let i = 1; i <= MAX; i++) {
      if (!names.includes(`icon ${i}`)) break;
      total++;
    }

    return total;
  }

  function activeTab() {
    return text(
      tabGroup()?.querySelector(
        '.pplr_tab_index.pplr_active'
      )
    );
  }

  function openTab(name) {
    const activate = () => {
      const tab = tabs().find(t =>
        text(t) === clean(name)
      );

      if (
        tab &&
        activeTab() !== clean(name)
      ) {
        tab.click();
      }
    };

    activate();

    setTimeout(() => {
      if (activeTab() !== clean(name)) {
        activate();
      }

      refreshActive();
    }, 100);

    setTimeout(() => {
      refresh(true);
    }, 250);
  }

  function field(index) {
    return document.querySelector(
      `.product-personalizer .pplr-wrapper.pplr-icon-${index + 1}`
    );
  }

  function selected(index) {
    const wrapper = field(index);
    if (!wrapper) return null;

    const swatches = [
      ...wrapper.querySelectorAll(
        '.pplr-swatch-element.selected'
      )
    ].filter(el =>
      !el.classList.contains('pplr_deselect')
    );

    const swatch = swatches.find(el =>
      el.getAttribute('data-val') ||
      el.getAttribute('data-value')
    );

    if (!swatch) return null;

    const value =
      swatch.getAttribute('data-val') ||
      swatch.getAttribute('data-value');

    const image =
      swatch.getAttribute('data-image') || '';

    return {
      index,
      wrapper,
      value,
      image: /^https:/i.test(image)
        ? image
        : ''
    };
  }

  function friendly(value) {
    let result = value || '';

    for (const suffix of SUFFIXES) {
      if (
        result.toLowerCase().endsWith(
          '-' + suffix.toLowerCase()
        )
      ) {
        result = result.slice(
          0,
          -(suffix.length + 1)
        );

        break;
      }
    }

    return result
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  function message(value = '') {
    const target = tray?.querySelector(
      '.timi-your-icons-message'
    );

    if (!target) return;

    target.textContent = value;
    target.hidden = !value;
  }

  /*
    Remove icons through Zepto's native control.
  */

  function removeIcon(index) {
    if (removing) return;

    const item = selected(index);
    if (!item) return;

    const remove = item.wrapper.querySelector(
      '.pplr-swatch-element.pplr_deselect'
    );

    if (!remove) {
      message(
        `Could not find Remove for Icon ${index + 1}.`
      );
      return;
    }

    removing = true;
    message('');

    remove.click();

    setTimeout(() => {
      removing = false;
      refresh(true);

      if (selected(index)) {
        message(
          `Check whether Icon ${index + 1} was removed.`
        );
      }
    }, 350);
  }

  /*
    Create the custom icon tray.
  */

  function createTray() {
    const root = document.createElement('section');

    root.className = 'timi-your-icons';
    root.setAttribute(
      'aria-label',
      'Your selected embroidery icons'
    );

    root.innerHTML = `
      <div class="timi-your-icons-heading">
        <span class="timi-your-icons-title">
          Your Icons
        </span>
        <span class="timi-your-icons-count">
          0 of 5 selected
        </span>
      </div>

      <button type="button"
        class="timi-edit-text" hidden>
        ✎ Edit Your Text
      </button>

      <p class="timi-icon-limit-note">
        Add up to 5 icons
      </p>

      <div class="timi-your-icons-grid"></div>

      <p class="timi-your-icons-message"
        aria-live="polite" hidden></p>
    `;

    root.addEventListener('click', event => {
      const remove = event.target.closest(
        '[data-timi-remove]'
      );

      if (remove) {
        event.preventDefault();
        event.stopPropagation();

        removeIcon(
          Number(remove.dataset.timiRemove)
        );

        return;
      }

      if (
        event.target.closest('.timi-edit-text')
      ) {
        event.preventDefault();
        openTab('Text');
        return;
      }

      if (
        event.target.closest('[data-timi-add-icon]')
      ) {
        event.preventDefault();
        addIcon();
        return;
      }

      const tile = event.target.closest(
        '[data-timi-icon-tab]'
      );

      if (tile) {
        event.preventDefault();

        openTab(
          `Icon ${
            Number(tile.dataset.timiIconTab) + 1
          }`
        );
      }
    });

    root.addEventListener('keydown', event => {
      if (
        !['Enter', ' '].includes(event.key)
      ) {
        return;
      }

      if (
        event.target.matches(
          '[data-timi-icon-tab]'
        )
      ) {
        event.preventDefault();

        openTab(
          `Icon ${
            Number(event.target.dataset.timiIconTab) + 1
          }`
        );
      }
    });

    return root;
  }

  function ensureTray() {
    const group = tabGroup();

    if (!group?.parentNode) return false;

    if (!tray) {
      tray = createTray();
    }

    document.querySelectorAll(
      '.timi-your-icons'
    ).forEach(element => {
      if (element !== tray) {
        element.remove();
      }
    });

    if (
      tray.parentNode !== group.parentNode ||
      tray.nextElementSibling !== group
    ) {
      group.before(tray);
    }

    return true;
  }

  /*
    Individual icon card.
  */

  function makeTile(item, index) {
    const tile = document.createElement('div');

    tile.className =
      'timi-your-icon-tile ' +
      (item ? 'is-selected' : 'is-empty');

    tile.dataset.timiIconTab = String(index);
    tile.setAttribute('role', 'button');
    tile.tabIndex = 0;

    const header = document.createElement('div');
    header.className = 'timi-your-icon-top';

    const number = document.createElement('span');
    number.className = 'timi-your-icon-number';
    number.textContent = `Icon ${index + 1}`;

    header.appendChild(number);

    if (item) {
      const remove =
        document.createElement('button');

      remove.type = 'button';
      remove.className =
        'timi-your-icon-remove';

      remove.dataset.timiRemove =
        String(index);

      remove.textContent = '×';

      remove.setAttribute(
        'aria-label',
        `Remove Icon ${index + 1}`
      );

      header.appendChild(remove);
    }

    tile.appendChild(header);

    const artwork =
      document.createElement('div');

    artwork.className = 'timi-your-icon-art';

    if (item?.image) {
      const image =
        document.createElement('img');

      image.src = item.image;
      image.alt = friendly(item.value);
      image.loading = 'lazy';

      artwork.appendChild(image);
    } else {
      artwork.textContent = item ? '✓' : '+';
    }

    tile.appendChild(artwork);

    const label = document.createElement('div');
    label.className = 'timi-your-icon-label';

    label.textContent = item
      ? friendly(item.value)
      : 'Not selected';

    tile.appendChild(label);

    return tile;
  }

  /*
    Small circular + button.
  */

  function makePlus() {
    const button =
      document.createElement('button');

    button.type = 'button';
    button.className = 'timi-add-icon-tile';
    button.dataset.timiAddIcon = '';
    button.textContent = '+';
    button.title = 'Add another icon';

    button.setAttribute(
      'aria-label',
      'Add another icon'
    );

    return button;
  }

  function addIcon() {
    const max = count();

    if (expanded >= max) return;

    const next = expanded + 1;

    if (
      !tabs().some(tab =>
        text(tab) === `icon ${next}`
      )
    ) {
      message(
        `Icon ${next} is unavailable.`
      );
      return;
    }

    expanded = next;

    refresh(true);
    openTab(`Icon ${next}`);
  }

  function refreshActive() {
    if (!tray || tray.hidden) return;

    const active = activeTab();

    tray.querySelectorAll(
      '[data-timi-icon-tab]'
    ).forEach(tile => {
      const number =
        Number(tile.dataset.timiIconTab) + 1;

      const editing =
        active === `icon ${number}`;

      tile.classList.toggle(
        'is-editing',
        editing
      );

      tile.setAttribute(
        'aria-pressed',
        String(editing)
      );
    });

    const edit = tray.querySelector(
      '.timi-edit-text'
    );

    if (edit) {
      edit.classList.toggle(
        'is-editing',
        active === 'text'
      );
    }
  }

  function refresh(force = false) {
    if (!ensureTray()) return;

    const valid = supported();

    tray.hidden = !valid;

    tray.style.setProperty(
      'display',
      valid ? 'block' : 'none',
      'important'
    );

    if (!valid) return;

    const max = count();
    if (!max) return;

    const items = Array.from(
      { length: max },
      (_, index) => selected(index)
    );

    const lastSelected = items.reduce(
      (last, item, index) =>
        item ? index + 1 : last,
      0
    );

    const match = activeTab().match(
      /^icon ([1-5])$/
    );

    const activeIndex = match
      ? Number(match[1])
      : 0;

    // Preserve expanded slots and all selections.
    expanded = Math.min(
      max,
      Math.max(
        1,
        expanded,
        lastSelected,
        activeIndex
      )
    );

    const nowMode = mode();

    if (nowMode !== lastMode) {
      lastMode = nowMode;
      force = true;
    }

    tray.querySelector(
      '.timi-your-icons-title'
    ).textContent = textAndIcons()
      ? 'Your Design'
      : 'Your Icons';

    const edit = tray.querySelector(
      '.timi-edit-text'
    );

    edit.hidden = !textAndIcons();

    const note = tray.querySelector(
      '.timi-icon-limit-note'
    );

    note.textContent =
      `Add up to ${max} icons`;

    const nextSignature = [
      nowMode,
      max,
      expanded,
      ...items.map(item =>
        item
          ? `${item.value}|${item.image}`
          : '-'
      )
    ].join('::');

    if (
      force ||
      nextSignature !== signature
    ) {
      signature = nextSignature;

      tray.querySelector(
        '.timi-your-icons-count'
      ).textContent =
        `${
          items.filter(Boolean).length
        } of ${max} selected`;

      const tiles = items
        .slice(0, expanded)
        .map((item, index) =>
          makeTile(item, index)
        );

      if (expanded < max) {
        tiles.push(makePlus());
      }

      tray.querySelector(
        '.timi-your-icons-grid'
      ).replaceChildren(...tiles);
    }

    refreshActive();
  }

  function schedule() {
    if (scheduled) return;

    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      refresh();
    });
  }

  /*
    Monitor Zepto selections and mode changes.
  */

  function start() {
    const style =
      document.createElement('style');

    style.id = 'timi-small-add-icon-v27';

    style.textContent = `
      .product-personalizer .timi-add-icon-tile {
        appearance: none !important;
        width: 34px !important;
        height: 34px !important;
        min-width: 34px !important;
        min-height: 34px !important;
        padding: 0 !important;
        margin: auto 0 !important;
        justify-self: start !important;
        align-self: center !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        border: 1px solid #8f182f !important;
        border-radius: 50% !important;
        background: #fff1f5 !important;
        color: #8f182f !important;
        font-size: 23px !important;
        line-height: 1 !important;
        cursor: pointer !important;
      }

      .product-personalizer
      .timi-add-icon-tile:hover {
        background: #f7bcd0 !important;
      }

      .product-personalizer
      .timi-add-icon-tile:focus-visible {
        outline: 2px solid #3e5e92 !important;
        outline-offset: 2px;
      }
    `;

    if (!document.getElementById(style.id)) {
      document.head.appendChild(style);
    }

    refresh(true);

    const observer = new MutationObserver(
      records => {
        const relevant = records.some(record => {
          const target = record.target;

          if (
            tray &&
            (
              target === tray ||
              tray.contains(target)
            )
          ) {
            return false;
          }

          if (record.type === 'attributes') {
            return !!target.closest?.(
              '.product-personalizer .pplr-wrapper, ' +
              '.product-personalizer .pplr_tab_wrapper'
            );
          }

          if (record.type === 'childList') {
            if (
              target.closest?.(
                '.product-personalizer'
              )
            ) {
              return true;
            }
          }

          return false;
        });

        if (relevant) schedule();
      }
    );

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'class',
        'style',
        'data-val',
        'data-value'
      ]
    });

    document.addEventListener(
      'click',
      event => {
        if (
          event.target.closest?.(
            '.product-personalizer .pplr-drop-item, ' +
            '.product-personalizer .pplr-swatch-element, ' +
            '.product-personalizer .pplr_tab_index'
          )
        ) {
          setTimeout(
            () => refresh(true),
            80
          );

          setTimeout(
            () => refresh(true),
            250
          );
        }
      },
      true
    );

    window.addEventListener(
      'pageshow',
      () => refresh(true)
    );

    setTimeout(
      () => refresh(true),
      600
    );

    setTimeout(
      () => refresh(true),
      1400
    );

    console.log(
      'TIMI Selected Icons v2.7 initialized'
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();
